<script setup lang="ts">
import { cloneDeep } from 'lodash';
import { useDialogPluginComponent } from 'quasar';
import { ref, toRef } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import type { ContextDto, CustomPageDto, Locales } from '@/services/types/api.types';
import { useCommunityStore } from '@/stores';
import { useLanguageValidationErrors } from '@/composables';

import LanguageSwitcher from './LanguageSwitcher.vue';
import LocalizedEditor from './base/LocalizedEditor.vue';
import LocalizedInput from './LocalizedInput.vue';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { localizableString } from '@/helpers/validation.helper';
import StandardInput from './StandardInput.vue';
import { useI18n } from 'vue-i18n';
import { ensureLocalizableString } from '@/helpers/form.helper';
import { provideEditLocale } from '@/composables/data-display-utils';

const communityStore = useCommunityStore();
const { t } = useI18n();

const props = defineProps<{
  customPage: CustomPageDto;
  languageEnabled: Locales[];
}>();
defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const initialValues = props.customPage;
ensureLocalizableString(initialValues, 'content', props.languageEnabled);

const { handleSubmit, errors, meta } = useForm({
  initialValues: initialValues,
  validationSchema: yup.object<ContextDto>({
    content: localizableString(props.languageEnabled),
    slug: yup.string().required(),
  }),
});
const languageErrors = useLanguageValidationErrors(errors);

const editModel = ref(cloneDeep(props.customPage));

const editLocale = provideEditLocale(toRef(() => props.languageEnabled));
const customPageTypes = communityStore.getCustomPageTypes();
const typeSelected = ref(editModel.value.type);

function typeChange(value: any) {
  typeSelected.value = value;
}

const onSubmit = handleSubmit(async (values) => {
  console.debug('onSubmit', values);
  await wrapSavingOperation(
    async () => await communityStore.saveCustomPages(values),
    () => onDialogOK(),
  );
});
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" v-if="editModel" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ t('form.dialogs.custom-page.edit') }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :languageEnabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section v-if="customPage.type != 1">
        <localized-select
          name="type"
          label="Type"
          :options="customPageTypes"
          emit-value
          map-options
          @change="typeChange"
        />
        <localized-input name="title" label="Titre" :editLocale="editLocale" :language_enabled="languageEnabled" />
        <standard-input name="slug" :label="$t('custom_page.fields.slug')" />
      </q-card-section>
      <q-card-section>
        <q-field label="Contenu" stack-label>
          <template v-slot:control>
            <localized-editor
              name="content"
              label="Content"
              class="q-mt-sm full-width"
              :editLocale="editLocale"
              :language_enabled="languageEnabled"
            />
          </template>
        </q-field>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="$t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="$t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<style></style>
